import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { AddressElement, Elements } from "@stripe/react-stripe-js";
import Modal from "../../design-system/components/modals/Modal";
import { PrimaryButton, SecondaryButton } from "../../design-system/components/buttons";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import { apiFetch } from "../../core/utils/api";

/**
 * Self-service "Update billing address" modal.
 *
 * Stripe Tax computes off `Customer.address`, NOT the card. Updating the
 * address here means the *next* invoice picks up the new tax jurisdiction;
 * the current period is unchanged.
 */

const API_ENDPOINTS = {
  STRIPE_CONFIG: "/api/stripe/config/",
  BILLING_ADDRESS: "/api/stripe/billing-address/update/",
};

const getStripePublishableKeyFromEnv = () =>
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE ||
  "";

const UpdateBillingAddressModal = ({
  isOpen,
  onClose,
  currentAddress,
  onUpdated,
}) => {
  const [addressComplete, setAddressComplete] = useState(false);
  const [addressValue, setAddressValue] = useState(currentAddress || null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [publishableKey, setPublishableKey] = useState(() => getStripePublishableKeyFromEnv());

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  const isDarkMode =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const stripeAppearance = useMemo(
    () => ({
      theme: isDarkMode ? "night" : "stripe",
      variables: {
        colorPrimary: isDarkMode ? "#60a5fa" : "#2563eb",
        colorBackground: isDarkMode ? "#121212" : "#ffffff",
        colorText: isDarkMode ? "#d4d4d4" : "#111827",
      },
    }),
    [isDarkMode]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSubmitting(false);
    setSubmitError(null);
    setAddressComplete(false);
    setAddressValue(currentAddress || null);
  }, [isOpen, currentAddress]);

  useEffect(() => {
    if (!isOpen) return;
    if (publishableKey) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch(API_ENDPOINTS.STRIPE_CONFIG, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await resp.json().catch(() => ({}));
        const key = String(data?.publishable_key || "").trim();
        if (!cancelled && resp.ok && key) setPublishableKey(key);
      } catch {
        // surfaced through the missing-Stripe state
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, publishableKey]);

  const normalizedCountry = String(addressValue?.address?.country || "")
    .trim()
    .toUpperCase();
  const normalizedPostal = String(addressValue?.address?.postal_code || "").trim();
  const canSubmit = Boolean(addressComplete) && Boolean(normalizedCountry) && Boolean(normalizedPostal);

  const handleSave = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const addr = addressValue?.address || {};
      const resp = await apiFetch(API_ENDPOINTS.BILLING_ADDRESS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country: normalizedCountry,
          postal_code: normalizedPostal,
          city: addr.city || "",
          state: addr.state || "",
          line1: addr.line1 || "",
          line2: addr.line2 || "",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Could not update your billing address.");
      }
      onUpdated?.(data?.billing_address || null);
      onClose?.();
    } catch (e) {
      setSubmitError(e?.message || "Could not update your billing address.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!submitting) onClose?.();
      }}
      title="Update billing address"
      size="md"
      closeOnOverlayClick={!submitting}
      closeOnEsc={!submitting}
      showCloseButton={!submitting}
      footer={
        <>
          <SecondaryButton type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSave}
            loading={submitting}
            disabled={!canSubmit || submitting}
          >
            Save address
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <p className={`${typography.bodySmall} ${textColors.secondary}`}>
          We use this address to calculate the tax on your next invoice. Your current period stays
          unchanged.
        </p>

        {submitError ? (
          <div
            role="alert"
            className={`rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2`}
          >
            <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{submitError}</p>
          </div>
        ) : null}

        {!stripePromise ? (
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading address form…</div>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{ appearance: stripeAppearance, mode: "setup", currency: "usd" }}
          >
            <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
              <AddressElement
                options={{
                  mode: "billing",
                  defaultValues: currentAddress
                    ? { address: currentAddress }
                    : { address: { country: "US" } },
                }}
                onChange={(e) => {
                  setAddressComplete(Boolean(e?.complete));
                  setAddressValue(e?.value || null);
                  if (submitError) setSubmitError(null);
                }}
              />
            </div>
          </Elements>
        )}
      </div>
    </Modal>
  );
};

export default UpdateBillingAddressModal;
